import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { retryLouiseQuery, useApiClient } from "@louise/api/louise-api";
import { throwApiError } from "@louise/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";

type SaveCoveragePayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}/coverage"]["patch"]
>;

type CoverageResponse = NonNullable<
  ResponseBody<
    paths["/{tenantId}/patients/{patientId}/coverage"]["get"]
  >["data"]
>;

type CoverageRequest = CoverageResponse["coverageRequests"][number];

export type CoverageState = "empty" | "active" | "expired";

export interface PolicyHolder {
  id: string;
  fullName: string;
  dateOfBirth: string | null;
  sexAssignedAtBirth: string | null;
  phoneNumber: string | null;
  emailAddress: string | null;
  relationship: string;
  streetAddress: string | null;
  city: string | null;
  country: string | null;
  postalCode: string | null;
}

export interface Employer {
  id: string;
  name: string;
  ein: string | null;
}

export interface Payer {
  id: string;
  name: string;
  displayName?: string | null;
}

export interface InsurancePolicy {
  id: string;
  insuranceCarrier: string | null;
  payerId?: string | null;
  memberId: string;
  groupNumber: string | null;
  planName: string | null;
  planType: string | null;
  effectiveDate?: string | null;
  terminationDate?: string | null;
  policyHolder?: PolicyHolder | null;
  employer?: Employer | null;
  payer?: Payer | null;
}

export interface PaymentConsent {
  id: string;
  paymentConsent: boolean;
}

// Fertility coverage is nested under coverageRequests[].results[] in the API
// response, never at the top level. Derive the type from the generated schema so
// contract drift is caught by tsc rather than masked by a hand-written interface.
export type FertilityCoverage = NonNullable<
  CoverageRequest["results"][number]["fertilityCoverage"]
>;

export type TreatmentLimit = FertilityCoverage["treatmentLimits"][number];

export type VerificationStatus = "unverified" | "pending" | "verified";

export interface CoverageData {
  userInsurancePolicies?: InsurancePolicy[];
  paymentConsent?: PaymentConsent | null;
  coverageRequests?: CoverageRequest[];
  verificationStatus?: VerificationStatus | null;
}

/**
 * Selects the fertility coverage to display from the nested coverage-request
 * results. Prefers verified results and, within those, the most recently created.
 */
function selectFertilityCoverage(
  coverageRequests: CoverageRequest[] | undefined,
): FertilityCoverage | null {
  const results = (coverageRequests ?? []).flatMap(
    (request) => request.results ?? [],
  );
  const withCoverage = results.filter(
    (result) => result.fertilityCoverage != null,
  );
  if (withCoverage.length === 0) return null;

  const verified = withCoverage.filter(
    (result) => result.status === "verified",
  );
  const pool = verified.length > 0 ? verified : withCoverage;
  const latest = pool.reduce((mostRecent, result) =>
    new Date(result.createdAt).getTime() >=
    new Date(mostRecent.createdAt).getTime()
      ? result
      : mostRecent,
  );

  return latest.fertilityCoverage ?? null;
}

/**
 * Returns coverage query options for use with useQuery.
 * Exported for reuse with custom select transforms or prefetching.
 */
export function useCoverageQueryOptions() {
  const { client, tenantId, patientId } = useApiClient();

  return {
    queryKey: ["coverage", tenantId, patientId] as const,
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}/coverage",
        {
          params: { path: { tenantId, patientId } },
          // The nullable no-coverage contract is version-gated so outdated
          // binaries keep the 404 they were built against; the generated
          // client does not model the x-api-version parameter, so pin it at
          // runtime through the request options.
          headers: { "x-api-version": "2026-08-27" },
        },
      );
      // The generated client is typed from the unversioned default OpenAPI
      // document, but this request pins x-api-version 2026-08-27, whose
      // no-coverage payload is null. Reintroduce that nullability before
      // normalizing so the compiler enforces the empty-object fallback.
      if (data) {
        const coverage = data.data as CoverageResponse | null;
        return (coverage ?? {}) as CoverageData;
      }

      throwApiError(
        error,
        "Failed to load coverage",
        "useCoverage",
        response?.status,
      );
    },
    enabled: !!tenantId && !!patientId,
    // Coverage is an enhancement to the profile, not a reason to leave its
    // entry point visually pending through several default retry intervals.
    // One retry handles a transient request while surfacing the existing retry
    // UI promptly for a genuine failure.
    retry: (failureCount: number, error: unknown) =>
      failureCount < 1 && retryLouiseQuery(failureCount, error),
  };
}

export function useCoverage() {
  const queryOptions = useCoverageQueryOptions();
  const query = useQuery(queryOptions);

  const policies = query.data?.userInsurancePolicies ?? [];
  const primaryPolicy = policies[0];

  // Derive state from actual data
  let state: CoverageState = "empty";

  if (primaryPolicy) {
    const terminationDate = primaryPolicy.terminationDate;
    if (terminationDate && new Date(terminationDate) < new Date()) {
      state = "expired";
    } else {
      state = "active";
    }
  }

  return {
    ...query,
    state,
    policies,
    primaryPolicy,
    paymentConsent: query.data?.paymentConsent ?? null,
    hasInsurance: policies.length > 0,
    fertilityCoverage: selectFertilityCoverage(query.data?.coverageRequests),
    verificationStatus:
      (query.data?.verificationStatus as VerificationStatus) ?? "unverified",
  };
}

/**
 * Returns a stable boolean indicating if user has valid coverage for onboarding.
 * Uses TanStack Query's `select` to only re-render when the boolean changes,
 * not when other coverage data changes.
 */
export function useHasCoverageForOnboarding() {
  const queryOptions = useCoverageQueryOptions();

  return useQuery({
    ...queryOptions,
    select: (data) => {
      const policies = data?.userInsurancePolicies ?? [];
      return policies.some(
        (policy) =>
          policy.memberId && (policy.payerId || policy.insuranceCarrier),
      );
    },
  });
}

/**
 * Mutation hook to save coverage data.
 * Handles both create (POST) and update (PATCH) based on existing coverage.
 */
export function useSaveCoverage() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveCoveragePayload) => {
      // Check if coverage exists to determine POST vs PATCH
      const {
        data: existingCoverage,
        error: existingCoverageError,
        response: existingCoverageResponse,
      } = await client.GET("/{tenantId}/patients/{patientId}/coverage", {
        params: {
          path: { tenantId, patientId },
        },
        headers: { "x-api-version": "2026-08-27" },
      });

      // A successful null response means this is a new coverage record. Never
      // turn a transport or authorization failure into a POST, which could
      // otherwise create a duplicate or hide the original error from the form.
      if (existingCoverageError) {
        throwApiError(
          existingCoverageError,
          "Failed to determine coverage state",
          "useSaveCoverage",
          existingCoverageResponse?.status,
        );
      }

      // Same unversioned-document typing gap as the read path: under the
      // pinned 2026-08-27 contract a missing record arrives as a 200 null,
      // which is exactly the signal that selects POST over PATCH.
      const existing = (existingCoverage?.data ??
        null) as CoverageResponse | null;

      if (existing) {
        const { data, error, response } = await client.PATCH(
          "/{tenantId}/patients/{patientId}/coverage",
          {
            params: { path: { tenantId, patientId } },
            body: payload,
          },
        );
        if (error) {
          throwApiError(
            error,
            "Failed to save coverage",
            "useSaveCoverage",
            response?.status,
          );
        }
        return data?.data;
      } else {
        const { data, error, response } = await client.POST(
          "/{tenantId}/patients/{patientId}/coverage",
          {
            params: { path: { tenantId, patientId } },
            body: payload,
          },
        );
        if (error) {
          throwApiError(
            error,
            "Failed to save coverage",
            "useSaveCoverage",
            response?.status,
          );
        }
        return data?.data;
      }
    },
    onSuccess: () => {
      // Invalidate coverage queries to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: ["coverage", tenantId, patientId],
      });
    },
    onError: (error) => {
      console.error("Failed to save coverage:", error);
    },
  });
}
